import {
  ContractCategory,
  ContractExportReportFormat,
  ContractListSortBy,
  ContractStatus,
  GenerateContractExportReportRequest,
  GenerateContractExportZipRequest,
} from 'compliance-api/generated';
import { useCallback, useMemo } from 'react';
import {
  useGenerateContractsExportReportMutation,
  useGenerateContractsExportZipMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import { Button, DropdownMenu } from 'ui';
import { downloadFile, openInNewTab } from 'ui/utils';

type ContractsExportFilters = {
  searchValue: string;
  status: ContractStatus | null;
  partyId: number | null;
  category: ContractCategory | null;
  executionDateRange: [string, string] | null;
  sortBy: ContractListSortBy;
  archive: boolean;
};

const REPORT_FORMAT_OPTIONS: { label: string; value: ContractExportReportFormat }[] = [
  { label: 'PDF', value: ContractExportReportFormat.Pdf },
  { label: 'XLSX', value: ContractExportReportFormat.Xlsx },
];

interface ContractExportActionsProps {
  filters: ContractsExportFilters;
}

const ContractExportActions = ({ filters }: ContractExportActionsProps) => {
  const { mutateAsync: generateContractsZip, isLoading: isContractsZipLoading } =
    useGenerateContractsExportZipMutation();
  const { mutateAsync: generateContractsReport, isLoading: isContractsReportLoading } =
    useGenerateContractsExportReportMutation();

  const exportPayload = useMemo<
    Omit<GenerateContractExportReportRequest, 'format'> & GenerateContractExportZipRequest
  >(
    () => ({
      search: filters.searchValue || undefined,
      status: filters.status || undefined,
      party_id: filters.partyId || undefined,
      category: filters.category || undefined,
      execution_date_from: filters.executionDateRange?.[0],
      execution_date_to: filters.executionDateRange?.[1],
      sort_by: filters.sortBy,
      is_active: !filters.archive,
    }),
    [filters],
  );

  const isLoading = isContractsZipLoading || isContractsReportLoading;

  const handleDownloadContractsFiles = useCallback(async () => {
    const result = await generateContractsZip(exportPayload);
    if (result?.blob) {
      downloadFile(result.blob, result.filename || 'contracts.zip');
    }
  }, [exportPayload, generateContractsZip]);

  const handleGenerateContractsReport = useCallback(
    async (format: ContractExportReportFormat) => {
      const result = await generateContractsReport({
        ...exportPayload,
        format,
      });

      if (!result?.blob) return;

      if (format === ContractExportReportFormat.Pdf) {
        const blobUrl = URL.createObjectURL(result.blob);
        openInNewTab(blobUrl);
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000 * 60);
        return;
      }

      downloadFile(result.blob, result.filename || 'contracts-report.xlsx');
    },
    [exportPayload, generateContractsReport],
  );

  return (
    <>
      <Button className="w-fit gap-2" onClick={handleDownloadContractsFiles} disabled={isLoading}>
        <span>Download all contracts files</span>
      </Button>

      <DropdownMenu
        className="w-[96px]"
        itemClassName="!px-3 !py-2"
        containerClassName="w-fit"
        placement="bottom-end"
        disabled={isLoading}
        items={REPORT_FORMAT_OPTIONS.map(option => ({
          label: option.label,
          onClick: () => handleGenerateContractsReport(option.value),
        }))}
        button={
          <Button className="w-fit gap-2" disabled={isLoading}>
            <span>Generate report</span>
          </Button>
        }
      />
    </>
  );
};

export default ContractExportActions;
